import { useLocation, useParams } from "react-router-dom";
import { useAppState } from "@/modules/app/useAppContext";
import { useState } from "react";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import { PAGE_SIZE_OPTIONS } from "@/pages/query/constants";
import { WorkflowType } from "@/pages/query/types";
import { useQuery } from "@tanstack/react-query";
import { getSnowflakeJobRunsGraphData, getSnowflakeJobsSummary } from "@/helpers/apis";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { getWorkflowNavigationPrefix } from "@/pages/query/snowflake/utils";
import { formatCost, formatMilliseconds } from "@/helpers/utils";
import { RelativeComponentLoader } from "@/components/loader";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Label,
  NativeSelect,
  Stack,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Typography,
} from "@altimateai/lego";
import { Graph } from "@/pages/query/Graph";
import { RunHistory } from "@/pages/query/snowflake/RunHistory";
import { TasksList } from "@/pages/query/snowflake/TasksList";

export const JobDetails = () => {
  const { workflowId } = useParams();
  const { currency } = useAppState();
  const { startDate, endDate, navigateTo } = useDateFromSearchParams();
  const [graphSize, setGraphSize] = useState(PAGE_SIZE_OPTIONS[0].value);
  const location = useLocation();

  // Determine workflow type from the current path
  const workflowType = location.pathname.includes("/tableau-jobs")
    ? WorkflowType.TABLEAU
    : WorkflowType.CUSTOM;

  const {
    data: summaryData,
    isLoading: isSummaryLoading,
    error: summaryError,
  } = useQuery({
    queryKey: ["snowflake_job", workflowId, startDate, endDate, workflowType],
    queryFn: () =>
      getSnowflakeJobsSummary(encodeURIComponent(workflowId!), {
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
        workflow_type: workflowType,
      }),
  });

  const { data: graphData, isLoading: isGraphLoading } = useQuery({
    queryKey: ["snowflake_jobs_graph", workflowId, startDate, endDate],
    queryFn: () =>
      getSnowflakeJobRunsGraphData(encodeURIComponent(workflowId!), {
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
      }),
    staleTime: 500,
    enabled: Boolean(workflowId),
  });

  const setSelectedRun = (runId: string) => {
    navigateTo(
      `${getWorkflowNavigationPrefix(workflowType)}/${encodeURIComponent(workflowId!)}/${runId}`
    );
  };

  if (isSummaryLoading) {
    return <RelativeComponentLoader label="Loading..." />;
  }

  if (summaryError || !summaryData) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Failed to load job summary</AlertTitle>
        <AlertDescription>
          {(summaryError as Error)?.message ||
            "There was an error loading the job summary. Please try again later."}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Stack direction="column" className="gap-sm">
      <Stack className="al-items-center flex-wrap col-gap-xl">
        {[
          {
            value: formatMilliseconds(summaryData.execution_time / summaryData.runs),
            label: "Average runtime",
          },
          {
            value: formatMilliseconds(summaryData.percentile_90_runtime),
            label: "90th percentile runtime",
          },
          {
            value: formatMilliseconds(summaryData.max_runtime),
            label: "Max runtime",
          },
          {
            value: formatMilliseconds(summaryData.min_runtime),
            label: "Min runtime",
          },
          {
            value: summaryData.runs,
            label: "Total runs",
          },
          {
            value: (summaryData.success_runs / summaryData.runs).toFixed(2),
            label: "Success ratio",
          },
          {
            // @ts-ignore
            value: formatCost(summaryData.query_cost / summaryData.runs, currency),
            label: "Average cost",
          },
        ].map(item => {
          const labelId = `metric-${item.label.toLowerCase().replace(/\s+/g, "-")}`;
          return (
            <Stack key={item.label} className="al-items-center gap-xs">
              <div id={labelId} className="al-text-light-gray">
                {item.label}:
              </div>
              <div aria-labelledby={labelId} className="fw-semibold">
                {item.value}
              </div>
            </Stack>
          );
        })}
      </Stack>
      <Stack direction="column" className={"al-p-4 al-mt-2 al-border al-rounded-md"}>
        <Stack align="center">
          <Typography variant={"section-heading"}>Runs Cost</Typography>
          <div className="spacer" />
          {(graphData?.length || 0) > Number(PAGE_SIZE_OPTIONS[0].value) && (
            <Stack direction={"row"} align={"center"} gap={"md"}>
              <Label className={"al-m-0"}>Show Runs:</Label>
              <NativeSelect
                className={"al-min-w-36"}
                variant={"outline"}
                options={PAGE_SIZE_OPTIONS}
                value={graphSize}
                onChange={setGraphSize}
              />
            </Stack>
          )}
        </Stack>
        <Graph
          isLoading={isGraphLoading}
          data={graphData?.slice(0, Number(graphSize))?.toReversed() ?? []}
          setSelectedRun={setSelectedRun}
          annotationEnabled
        />
      </Stack>
      <Stack direction="column" className={"al-mt-4"}>
        <Stack align="center" justify={"between"}>
          <Typography variant={"section-heading"}>Runs List</Typography>
          <Button
            onClick={() =>
              navigateTo(
                `${getWorkflowNavigationPrefix(workflowType)}/${encodeURIComponent(workflowId!)}/compare`
              )
            }
          >
            Compare Runs
          </Button>
        </Stack>
        <Tabs defaultValue={"tasks"} variant={"underlined"}>
          <TabsList>
            <TabsTrigger value="tasks">Tasks</TabsTrigger>
            <TabsTrigger value="runs">Runs</TabsTrigger>
          </TabsList>
          <TabsContent value="tasks">
            <TasksList />
          </TabsContent>
          <TabsContent value="runs">
            <RunHistory />
          </TabsContent>
        </Tabs>
      </Stack>
    </Stack>
  );
};
